import { useEffect, useState } from 'react';
import { loadStudioType, type StudioField, type StudioManifest, type StudioType } from './types';

export function StudioProgramFields({ config, onChange }: {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const typeId = typeof config.studioType === 'string' ? config.studioType : '';
  const [type, setType] = useState<StudioType | null | undefined>(undefined);
  useEffect(() => {
    let cancelled = false;
    void loadStudioType(typeId).then((t) => { if (!cancelled) setType(t); });
    return () => { cancelled = true; };
  }, [typeId]);
  if (type === undefined) return null;
  if (type === null) return <p className="error" role="alert">This program uses a Studio game that is not available.</p>;
  return (
    <>
      <p className="field-label" style={{ marginBottom: 0 }}>{type.manifest.name}</p>
      <StudioFields manifest={type.manifest} config={config} onChange={onChange} />
    </>
  );
}

function rawOf(f: StudioField, v: unknown): string {
  if (f.type === 'words') return Array.isArray(v) ? v.map(String).join('\n') : '';
  if (f.type === 'number') return typeof v === 'number' ? String(v) : '';
  return typeof v === 'string' ? v : '';
}

function valueOf(f: StudioField, raw: string): unknown {
  if (f.type === 'words') return raw.split(/\n|,/).map((w) => w.trim()).filter(Boolean);
  if (f.type === 'number') return raw === '' ? f.fallback : Number(raw);
  if (f.type === 'choice') return raw || f.fallback;
  return raw;
}

export default function StudioFields({ manifest, config, onChange }: {
  manifest: StudioManifest;
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const fields = [...manifest.content, ...manifest.params];
  const [raw, setRaw] = useState<Record<string, string>>(() => Object.fromEntries(fields.map((f) => [f.id, rawOf(f, config[f.id])])));

  const set = (f: StudioField, text: string) => {
    setRaw((r) => ({ ...r, [f.id]: text }));
    onChange({ ...config, studioType: manifest.id, [f.id]: valueOf(f, text) });
  };

  return (
    <>
      {fields.map((f) => {
        const id = `${manifest.id}-${f.id}`;
        const v = raw[f.id] ?? '';
        return (
          <div className="field" key={f.id}>
            <label htmlFor={id}>{f.label}</label>
            {f.type === 'words' ? (
              <textarea id={id} rows={4} placeholder="One per line" value={v} onChange={(e) => set(f, e.target.value)} />
            ) : f.type === 'number' ? (
              <input id={id} type="number" min={f.min} max={f.max} placeholder={String(f.fallback)} value={v} onChange={(e) => set(f, e.target.value)} />
            ) : f.type === 'choice' ? (
              <select id={id} value={v || f.fallback} onChange={(e) => set(f, e.target.value)}>
                {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input id={id} value={v} maxLength={f.max ?? 500} onChange={(e) => set(f, e.target.value)} />
            )}
            {f.hint && <p className="field-hint">{f.hint}</p>}
          </div>
        );
      })}
    </>
  );
}
